import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CartItemResponse {
  cartUuid: string | null;
  cartStatus: string | null;
  cartTotals: Record<string, unknown> | null;
  itemUuid: string;
  itemType: string;
}

export interface CartItemSummary {
  uuid: string;
  type: string;
  pricing: {
    amount?: number;
    currency?: string;
    billingFrequency?: string;
  } | null;
  payload: Record<string, any> | null;
  createdAt?: string | null;
}

export interface CartSummary {
  uuid: string;
  status: string;
  currency: string;
  totals: Record<string, unknown> | null;
  items: CartItemSummary[];
  isEmpty: boolean;
}

export interface CartConfirmResponse {
  cartUuid: string | null;
  cartStatus: string | null;
  requests: CartConfirmRequestSummary[];
}

export interface CartConfirmRequestSummary {
  id: number;
  status: string;
  cartItemUuid: string;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly cartSubject = new BehaviorSubject<CartSummary | null>(null);
  readonly cart$ = this.cartSubject.asObservable();

  constructor(private readonly http: HttpClient) { }

  saveAddpItem(payload: unknown): Observable<CartItemResponse> {
    return this.http.post<CartItemResponse>(`${environment.baseUrlWs}/api/cart/addp`, payload).pipe(
      tap(() => this.refreshCart())
    );
  }

  updateItem(itemUuid: string, payload: unknown): Observable<CartItemResponse> {
    return this.http.put<CartItemResponse>(`${environment.baseUrlWs}/api/cart/items/${itemUuid}`, payload).pipe(
      tap(() => this.refreshCart())
    );
  }

  refreshCart(): void {
    this.fetchCart().subscribe({
      next: cart => this.cartSubject.next(cart),
      error: () => { /* silently ignore */ }
    });
  }

  ensureCartLoaded(): void {
    if (!this.cartSubject.value) {
      this.refreshCart();
    }
  }

  clearCart(): void {
    this.cartSubject.next(null);
  }

  private fetchCart(): Observable<CartSummary> {
    return this.http.get<CartSummary>(`${environment.baseUrlWs}/api/cart`);
  }

  deleteItem(itemUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.baseUrlWs}/api/cart/items/${itemUuid}`).pipe(
      tap(() => this.refreshCart())
    );
  }

  confirmOrder(): Observable<CartConfirmResponse> {
    return this.http.post<CartConfirmResponse>(`${environment.baseUrlWs}/api/cart/confirm`, {}).pipe(
      tap(() => this.refreshCart())
    );
  }
}
